import React, { useState } from 'react';

const GrammarChecker = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [correctedText, setCorrectedText] = useState('');

  const checkGrammar = async () => {
    if (!text.trim()) return;

    setLoading(true);
    setResult(null);
    setCorrectedText('');

    try {
      const response = await fetch('https://api.languagetoolplus.com/v2/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ text, language: 'en-US' })
      });

      const data = await response.json();
      setResult(data.matches);
    } catch (error) {
      setResult([{ message: 'Error checking grammar. Please try again later.' }]);
    } finally {
      setLoading(false);
    }
  };

  const generateCorrectedText = () => {
    if (!result || result.length === 0) return;

    let updatedText = text;
    let offsetShift = 0;

    result.forEach((issue) => {
      if (issue.replacements.length > 0) {
        const replacement = issue.replacements[0].value;
        const from = issue.offset + offsetShift;
        const to = from + issue.length;

        updatedText = updatedText.slice(0, from) + replacement + updatedText.slice(to);
        offsetShift += replacement.length - issue.length;
      }
    });

    setCorrectedText(updatedText);
  };

  return (
    <section className="min-h-screen bg-[#02042b] flex items-center justify-center px-4 py-12 mt-20">
      <div className="w-full max-w-3xl bg-white/10 backdrop-blur-lg text-white p-8 rounded-2xl shadow-2xl border border-white/20">
        <h1 className="text-3xl font-extrabold mb-6 text-center text-white">Free Grammar Checker</h1>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          className="w-full p-4 border border-white/20 bg-white/5 text-white placeholder-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Paste your text here..."
        ></textarea>

        <button
          onClick={checkGrammar}
          className="mt-6 w-full bg-gradient-to-r from-blue-600 to-teal-500 text-white font-semibold px-6 py-3 rounded-lg hover:scale-105 transition duration-300"
        >
          {loading ? 'Checking...' : 'Check Grammar'}
        </button>

        {result && (
          <div className="mt-6">
            {result.length === 0 ? (
              <p className="text-green-400 text-lg font-semibold text-center">No issues found!</p>
            ) : (
              <>
                <ul className="list-disc pl-5 space-y-3">
                  {result.map((issue, idx) => (
                    <li key={idx} className="bg-white/10 p-3 rounded-md">
                      <p className="text-red-300 font-semibold">{issue.message}</p>
                      {issue.replacements && issue.replacements.length > 0 && (
                        <p className="text-sm text-gray-200 mt-1">
                          <span className="text-gray-400">Suggestions:</span> {issue.replacements.map(r => r.value).join(', ')}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={generateCorrectedText}
                  className="mt-6 w-full bg-green-600 text-white font-semibold px-6 py-3 rounded-lg hover:scale-105 transition duration-300"
                >
                  Show Corrected Text
                </button>
              </>
            )}
          </div>
        )}

        {correctedText && (
          <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <h3 className="text-white font-bold mb-2">Corrected Text:</h3>
            <p className="text-green-300 whitespace-pre-line">{correctedText}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default GrammarChecker;
